import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import * as motion from 'motion/react-m';
import {
	AnimatePresence,
	type MotionValue,
	type SpringOptions,
	useMotionValue,
	useSpring,
	useTransform,
} from 'motion/react';
import {
	Children,
	cloneElement,
	createContext,
	useContext,
	useMemo,
	useRef,
} from 'react';
import { MotionProvider } from './motion';

const DEFAULT_MAGNIFICATION = 50;
const DEFAULT_DISTANCE = 100;
const DEFAULT_PANEL_HEIGHT = 64;
const DEFAULT_SPRING: SpringOptions = {
	mass: 0.1,
	stiffness: 150,
	damping: 12,
};

type DockProps = {
	children: React.ReactNode;
	className?: string;
	distance?: number;
	panelHeight?: number;
	magnification?: number;
	spring?: SpringOptions;
	vertical?: boolean;
	visible?: boolean;
};
type DockItemProps = React.ComponentProps<typeof motion.div> & {
	magnification?: number;
};
type DockIconProps = { className?: string; children: React.ReactNode };
type DocContextType = {
	mouseX: MotionValue;
	spring: SpringOptions;
	magnification: number;
	distance: number;
	vertical: boolean;
};

const DockContext = createContext<DocContextType | undefined>(undefined);

function useDock() {
	const ctx = useContext(DockContext);
	if (!ctx) throw new Error('useDock must be used within a DockProvider');
	return ctx;
}

function Dock({
	children,
	className,
	spring = DEFAULT_SPRING,
	magnification = DEFAULT_MAGNIFICATION,
	distance = DEFAULT_DISTANCE,
	panelHeight = DEFAULT_PANEL_HEIGHT,
	vertical = false,
	visible = true,
}: DockProps) {
	const mouseX = useMotionValue(Number.POSITIVE_INFINITY);
	const isHovered = useMotionValue(0);
	const contextValue = useMemo(
		() => ({ mouseX, spring, distance, magnification, vertical }),
		[mouseX, spring, distance, magnification, vertical],
	);

	return (
		<MotionProvider feature="domMax">
			<div
				className={cn(
					'flex max-w-full overflow-auto',
					vertical
						? 'max-h-full items-start'
						: 'mx-2 items-end overflow-x-auto',
				)}
			>
				<motion.div
					onMouseMove={({ clientX, clientY }) => {
						isHovered.set(1);
						mouseX.set(vertical ? clientY : clientX);
					}}
					onMouseLeave={() => {
						isHovered.set(0);
						mouseX.set(Number.POSITIVE_INFINITY);
					}}
					className={cn(
						'relative mx-auto flex gap-2 rounded-2xl',
						vertical ? 'flex-col pt-3 pb-4.5' : 'items-center',
						className,
					)}
					style={vertical ? { width: panelHeight } : { height: panelHeight }}
					role="toolbar"
					aria-label={m.dock_aria()}
				>
					<DockContext.Provider value={contextValue}>
						{children}
					</DockContext.Provider>
					<AnimatePresence initial={false}>
						{visible && (
							<motion.div
								className="absolute inset-0 -z-10 rounded-full border border-foreground/10 bg-background"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.15 }}
							/>
						)}
					</AnimatePresence>
				</motion.div>
			</div>
		</MotionProvider>
	);
}

function DockItem({
	children,
	className,
	magnification: itemMagnification,
	...props
}: DockItemProps) {
	const ref = useRef<HTMLDivElement>(null);
	const {
		distance,
		magnification: dockMagnification,
		mouseX,
		vertical,
	} = useDock();
	const magnification = itemMagnification ?? dockMagnification;
	const isHovered = useMotionValue(0);
	const distanceCalc = useTransform(mouseX, (val: number) => {
		const bounds = ref.current?.getBoundingClientRect() ?? {
			x: 0,
			y: 0,
			width: 0,
			height: 0,
		};
		const center = vertical
			? bounds.y + bounds.height / 2
			: bounds.x + bounds.width / 2;
		return val - center;
	});
	const sizeSync = useTransform(
		distanceCalc,
		[-distance, 0, distance],
		[40, magnification, 40],
	);
	const size = useSpring(sizeSync, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	});

	return (
		<motion.div
			ref={ref}
			style={vertical ? { height: size } : { width: size }}
			onHoverStart={() => isHovered.set(1)}
			onHoverEnd={() => isHovered.set(0)}
			className={cn(
				'relative inline-flex items-center justify-center text-primary hover:bg-primary/10',
				className,
			)}
			tabIndex={0}
			role="button"
			{...props}
		>
			{Children.map(children as React.ReactElement, (child) =>
				cloneElement(child as React.ReactElement<Record<string, unknown>>, {
					[vertical ? 'height' : 'width']: size,
					isHovered,
				}),
			)}
		</motion.div>
	);
}

function DockIcon({ children, className, ...rest }: DockIconProps) {
	const restProps = rest as Record<string, unknown>;
	const size = (restProps.width ?? restProps.height) as MotionValue<number>;
	const sizeTransform = useTransform(size, (val) => val / 2);
	return (
		<motion.div
			className={className}
			style={{ width: sizeTransform, height: sizeTransform }}
			{...rest}
		>
			{children}
		</motion.div>
	);
}

export { Dock, DockIcon, DockItem };
